import React, { useState, useCallback, useMemo } from 'react'
import { Flex, InputNumber } from 'antd'
import Select, { SelectCompProps } from '../select/Select'
import styles from './SelectRange.module.less'
import { DataTestId, TextConstants } from 'src/constants/ui.constants'

const { EnterARange, Description } = TextConstants.SelectRange

export type SelectRangeProps = Omit<SelectCompProps, 'value' | 'onChange'> & {
    dropdownStyle?: React.CSSProperties
    max: number
    min: number
    onChange?: (value: [number, number] | undefined) => void
    value?: [number, number]
    step?: number
    precision?: number
    minLimit?: number
    maxLimit?: number
    onDropdownVisibleChange?: (visible: boolean) => void
}

const SelectRange: React.FC<SelectRangeProps> = props => {
    const [isRangeSelected, setIsRangeSelected] = useState<boolean>(
        props.value?.length ? true : false
    )
    const [isOpen, setIsOpen] = useState(false)
    const [currentValue, setCurrentValue] = useState<[number | null, number | null]>([
        props.value?.length ? props.value?.[0] : props.min,
        props.value?.length ? props.value?.[1] : props.max,
    ])
    const [finalValue, setFinalValue] = useState<[number, number] | undefined>(undefined)
    const [errors, setErrors] = useState<{ minLimit: boolean; maxLimit: boolean }>({
        minLimit: false,
        maxLimit: false,
    })

    const updateValue = useCallback(
        (min: number, max: number) => {
            if (props.onChange) {
                props.onChange([min, max])
            }
        },
        [props]
    )

    const handleMaxChange = useCallback((value: number | null) => {
        setCurrentValue(prev => [prev[0], value])
    }, [])

    const handleMinChange = useCallback((value: number | null) => {
        setCurrentValue(prev => [value, prev[1]])
    }, [])

    const handleClear = useCallback(() => {
        setIsRangeSelected(false)
        setCurrentValue([props.min, props.max])
        if (props.onChange) {
            props.onChange(undefined)
        }
        setFinalValue(undefined)
        setErrors({ minLimit: false, maxLimit: false })
    }, [props])

    const customStyle: React.CSSProperties = {
        height: props.height,
        width: props.width,
        ...props.style,
    }

    const getMinStatus = useCallback(
        (value: number, min: number): 'error' | undefined => {
            const error = value < min || value >= (currentValue[1] ?? props.max)
            setErrors(prevErrors => ({ ...prevErrors, minLimit: error }))
            return error ? 'error' : undefined
        },
        [currentValue, props.max]
    )

    const getMaxStatus = useCallback(
        (value: number, max: number): 'error' | undefined => {
            const error = value > max || value <= (currentValue[0] ?? props.min)
            setErrors(prevErrors => ({ ...prevErrors, maxLimit: error }))
            return error ? 'error' : undefined
        },
        [currentValue, props.min]
    )

    const getInputNumbers = useCallback(
        (isMin: boolean) => {
            const value = isMin ? currentValue[0] : currentValue[1]
            return {
                onChange: isMin ? handleMinChange : handleMaxChange,
                step: props.step ?? 1,
                value: value,
                status: isMin
                    ? getMinStatus(value ?? props.min, props.min)
                    : getMaxStatus(value ?? props.max, props.max),
                type: 'number',
            }
        },
        [
            currentValue,
            handleMinChange,
            handleMaxChange,
            props.step,
            props.min,
            props.max,
            getMinStatus,
            getMaxStatus,
        ]
    )

    const getErrorMessage = useCallback(() => {
        return `eg: ${props?.minLimit ?? props.min} - ${props?.maxLimit ?? props.max}`
    }, [props?.minLimit, props?.maxLimit, props.min, props.max])

    const dropdownContent = useMemo(() => {
        return (
            <div
                style={props.dropdownStyle}
                className={styles.dropdown}
                data-testid={DataTestId.SelectRangeDropdown}
            >
                <p className={styles.dropdownTitle}>{EnterARange}</p>
                <p className={styles.dropdownSubtitle}>{Description}</p>
                <Flex align="center" gap="4px" justify="space-between">
                    <InputNumber
                        {...getInputNumbers(true)}
                        data-testid={DataTestId.SelectRangeInputMin}
                    />
                    <p>-</p>
                    <InputNumber
                        {...getInputNumbers(false)}
                        data-testid={DataTestId.SelectRangeInputMax}
                    />
                </Flex>
                {errors.minLimit || errors.maxLimit ? (
                    <p className={styles.incorrectRangeErrorMessage}>
                        {`Invalid range. Select a range between ${props.min} and ${
                            props.max
                        } (${getErrorMessage()})`}
                    </p>
                ) : null}
            </div>
        )
    }, [
        errors.maxLimit,
        errors.minLimit,
        getInputNumbers,
        props.dropdownStyle,
        props.max,
        props.min,
        getErrorMessage,
    ])

    const getTruncatedValue = useCallback(
        (
            value: [number | null, number | null],
            numOfDecimalDigits: number,
            fallbackValue: [number, number]
        ) => {
            const pow = 10 ** numOfDecimalDigits
            const lowerValue = Math.trunc((value?.[0] ?? fallbackValue[0]) * pow) / pow
            const upperValue = Math.trunc((value?.[1] ?? fallbackValue[1]) * pow) / pow
            return [lowerValue, upperValue]
        },
        []
    )

    const getFinalValue = useCallback(
        (currentValueMin: number, currentValueMax: number, max: number): [number, number] => {
            if (currentValueMin < currentValueMax) {
                return [currentValueMin, currentValueMax]
            }
            const change = Math.pow(10, -1 * (props.precision ?? 0))
            const trialFinalMaxValue = Number(
                (currentValueMax + change).toFixed(props.precision ?? 0)
            )
            const trialFinalMinValue = Number(
                (currentValueMin - change).toFixed(props.precision ?? 0)
            )
            if (trialFinalMaxValue > max) {
                return [trialFinalMinValue, currentValueMax]
            }
            return [currentValueMin, trialFinalMaxValue]
        },
        [props.precision]
    )

    const handleKeyDown = useCallback(
        (event: { key: string }) => {
            if (event.key === 'Enter') {
                if (errors.minLimit || errors.maxLimit) {
                    return
                }
                setIsRangeSelected(true)
                setIsOpen(false)
                const truncatedValue = getTruncatedValue(currentValue, props.precision ?? 0, [
                    props.min,
                    props.max,
                ])
                const finalValue = getFinalValue(truncatedValue[0], truncatedValue[1], props.max)
                updateValue(finalValue[0], finalValue[1])
                setFinalValue(finalValue)
            }
        },
        [
            currentValue,
            errors.maxLimit,
            errors.minLimit,
            getFinalValue,
            getTruncatedValue,
            props.max,
            props.min,
            props.precision,
            updateValue,
        ]
    )

    return (
        <Select
            dropdownRender={() => dropdownContent}
            onDropdownVisibleChange={visible => {
                setCurrentValue([finalValue?.[0] ?? props.min, finalValue?.[1] ?? props.max])
                setIsOpen(visible)
                props.onDropdownVisibleChange?.(visible)
            }}
            placeholder={props.placeholder}
            value={isRangeSelected ? `${finalValue?.[0]} - ${finalValue?.[1]}` : undefined}
            style={customStyle}
            allowClear={props.allowClear}
            onClear={handleClear}
            handleKeyDown={handleKeyDown}
            isOpen={isOpen}
            shouldDisableTyping={true}
        />
    )
}

export default SelectRange
